// plan-13 S06: broadcast stage widget

import type { StageWidget, StageHandle, StageCtx } from '../stage/types'
import { generateBroadcast, type BroadcastLine } from './generator'
import { broadcastDate, utcDate } from '../../core/seed'
import { load, set } from '../../core/save'
import { play } from '../../audio/sfx'

const BROADCAST_DURATION_MS = 288_000

export const broadcastWidget: StageWidget = {
  id: 'broadcast',
  label: 'PA.BROADCAST // CONSENSUS TRANSMISSION',

  mount(host: HTMLElement, ctx: StageCtx): StageHandle {
    const todayKey = utcDate()
    const transmission = generateBroadcast(todayKey)
    const timing = broadcastDate()

    const container = document.createElement('div')
    container.className = 'vt-widget-body'
    container.style.cssText = `
      font-family: var(--vt-font-mono);
      font-size: clamp(10px, 0.95vw, 12px);
      color: var(--vt-term-green);
      padding: 12px;
      line-height: 1.32;
      height: 100%;
      overflow-x: hidden;
      overflow-y: auto;
      min-height: 0;
      word-wrap: break-word;
      overflow-wrap: break-word;
    `

    // plan-14 S02: broadcast banner mounts into stage header-rail (not a sibling row)
    const header = document.createElement('div')
    header.dataset.railOwner = 'broadcast'
    header.style.cssText = `
      color: var(--vt-amber);
      font-size: clamp(9px, 0.85vw, 11px);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
    `
    header.textContent = `· live · 00:00 elapsed / 04:48 total`
    const rail = ctx.headerRail()
    rail.appendChild(header)
    rail.classList.add('vt-stage-rail-active')

    // line stream area
    const stream = document.createElement('div')
    stream.style.cssText = 'display: flex; flex-direction: column; gap: 2px;'
    container.appendChild(stream)

    // footer
    const footer = document.createElement('div')
    footer.style.cssText = `
      color: var(--vt-green-dim);
      font-style: italic;
      margin-top: 8px;
      border-top: 1px solid var(--vt-green-dim);
      padding-top: 4px;
    `
    footer.textContent = `// archive: #broadcast=${todayKey}`
    container.appendChild(footer)

    host.appendChild(container)

    // stream lines at scheduled offsets
    let active = true
    const lineTimers: ReturnType<typeof setTimeout>[] = []
    let startTime = Date.now()
    let elapsedOffset = 0

    // if we're replaying (ctx.args[0] === 'replay'), use 4x cadence
    const isReplay = ctx.args[0] === 'replay'
    const speedMultiplier = isReplay ? 4 : 1

    // if replaying a specific date
    let transmissionToPlay = transmission
    if (isReplay && ctx.args[1]) {
      const replayDate = ctx.args[1]
      // check 7-day window
      const replayMs = new Date(replayDate + 'T00:00:00Z').getTime()
      const todayMs = new Date(todayKey + 'T00:00:00Z').getTime()
      const dayDiff = (todayMs - replayMs) / 86400000
      if (dayDiff > 7 || dayDiff < 0) {
        const errorLine = document.createElement('div')
        errorLine.textContent = '> archive purged. consensus: probably.'
        errorLine.style.color = 'var(--vt-green-dim)'
        stream.appendChild(errorLine)
        return {
          dismiss() { active = false; lineTimers.forEach(clearTimeout); container.remove() },
        }
      }
      transmissionToPlay = generateBroadcast(replayDate)
    } else if (isReplay) {
      // replay today
      transmissionToPlay = generateBroadcast(todayKey)
    } else if (timing.isBroadcastWindow) {
      // live — start from current offset
      elapsedOffset = timing.msSinceStart
      startTime = Date.now() - elapsedOffset
    }

    // play broadcast_open SFX
    play('broadcast_open')

    // update header with elapsed time
    const headerInterval = setInterval(() => {
      if (!active) return
      const elapsed = Date.now() - startTime
      const totalSec = Math.floor(elapsed / 1000)
      const mm = String(Math.floor(totalSec / 60)).padStart(2, '0')
      const ss = String(totalSec % 60).padStart(2, '0')
      const livePart = isReplay ? 'replay' : 'live'
      const speedPart = isReplay ? ` (${speedMultiplier}x)` : ''
      header.textContent = `· ${livePart}${speedPart} · ${mm}:${ss} elapsed / 04:48 total`
    }, 1000)

    // schedule each line
    for (const bline of transmissionToPlay.lines) {
      const delay = (bline.at_ms - elapsedOffset) / speedMultiplier
      if (delay < -1000) continue // already past

      const timer = setTimeout(() => {
        if (!active) return
        renderLine(stream, bline)

        // peak-emphasis amber flash
        if (bline.emphasis === 'peak') {
          play('node_tick')
          const flash = document.createElement('div')
          flash.style.cssText = `
            position: absolute; top: 0; left: 0; right: 0; bottom: 0;
            background: var(--vt-amber); opacity: 0.06;
            pointer-events: none; transition: opacity 480ms;
          `
          container.style.position = 'relative'
          container.appendChild(flash)
          setTimeout(() => { flash.style.opacity = '0' }, 0)
          setTimeout(() => flash.remove(), 500)
        }

        // auto-scroll
        container.scrollTop = container.scrollHeight
      }, Math.max(0, delay))

      lineTimers.push(timer)
    }

    // end-of-broadcast handler
    const endDelay = (BROADCAST_DURATION_MS - elapsedOffset) / speedMultiplier + 1000
    const endTimer = setTimeout(() => {
      if (!active) return

      play('broadcast_close')

      // close line
      const closeLine = document.createElement('div')
      closeLine.textContent = '> broadcast closed. consensus holds.'
      closeLine.style.color = 'var(--vt-king-green)'
      stream.appendChild(closeLine)
      container.scrollTop = container.scrollHeight

      // update save
      if (!isReplay) {
        const sv = load()
        set('broadcastsHeard', (sv.broadcastsHeard ?? 0) + 1)
        set('lastBroadcastDate', transmissionToPlay.dateKey)
      }

      // auto-dismiss after 30s
      setTimeout(() => {
        if (active) ctx.close()
      }, 30000)
    }, endDelay)
    lineTimers.push(endTimer)

    return {
      dismiss() {
        active = false
        clearInterval(headerInterval)
        lineTimers.forEach(clearTimeout)
        container.remove()
      },
    }
  },
}

function renderLine(parent: HTMLElement, line: BroadcastLine): void {
  const el = document.createElement('div')
  el.textContent = line.text

  const colorMap: Record<string, string> = {
    amber: 'var(--vt-amber)',
    'king-green': 'var(--vt-king-green)',
    'term-green': 'var(--vt-term-green)',
    rain: 'var(--vt-rain)',
    golden: 'var(--vt-golden)',
  }
  el.style.color = colorMap[line.color] ?? 'var(--vt-term-green)'
  parent.appendChild(el)
}
